import ViewerContainer from 'app/containers/ViewerContainer/ViewerContainer';
import clsx from 'clsx';
import useAmplitude from 'core/hooks/useAmplitude';
import IconBackground from 'core/modules/atoms/IconBackground';
import AttentionCard from 'core/modules/components/cards/AttentionCard';
import PdfContent from 'core/modules/components/contentsDetail/PdfContent/PdfContent';
import AssetImage from 'ui/atoms/AssetImage/AssetImage';
import Button from 'ui/atoms/Button/Button';
import ProgressBar from 'ui/atoms/ProgressBar/ProgressBar';
import Tag from 'ui/atoms/Tag/Tag';
import TextHtml from 'ui/atoms/TextHtml/TextHtml';
import { contentHasProgress, contentIsActivity } from 'core/utils/lessonItems';
import { darkenColor } from 'core/utils/styles';
import { CONTENT_TYPES, PDF_ENGINE } from 'core/variables/constant';
import { LESSON_ITEM_STATUS } from 'core/variables/lessonItems';
import PropTypes from 'prop-types';
import React from 'react';
import MaterialPreviewLink from './MaterialPreviewLink';
import MaterialPreviewViewer from './MaterialPreviewViewer';
import useMaterialPreviewBodyStudent from './useMaterialPreviewBodyStudent';

const MaterialPreviewBodyStudent = ({ t, course, material, onClickOpenDetailedView, evaluationPeriodPublished, lessonItemScore, isDialogTwoColumn }) => {
  const {
    image,
    jobGuid,
    jobStatus,
    content_type_guid,
    useTangerineViewerInPreview,
    content_guid,
    isDocument,
    shouldUsePdfViewer,
    buttonText,
    buttonStyle,
    didacticTypeCover,
    buttonIsDisabled,
    attentionCardIcon,
    attentionCardTitle,
    attentionCardIconText,
    attentionCardColor,
    attentionCardDuration,
    renderProgress,
    progress,
    activityStatus,
    getMetadataInfo,
    onClickOpenContent,
    onClickOpenInNewTab,
    onClickStartActivity,
  } = useMaterialPreviewBodyStudent({ course, material, evaluationPeriodPublished, onClickOpenDetailedView });
  const { sendAmplitudeEvent, AMPLITUDE_EVENT } = useAmplitude();
  const showStatus = content_type_guid === CONTENT_TYPES.XAPI ? false : true;

  function onClickOpen(material) {
    onClickOpenContent(material);
    const extraParams = { content_type: material.type, content_guid: material.content_guid, from: 'lesson' };
    sendAmplitudeEvent(AMPLITUDE_EVENT.OPEN_CONTENT, extraParams, course);
  }

  return (
    <div
      className={clsx('material-preview-body-student', {
        'material-preview-body-student--center-viewer': !isDocument,
        'material-preview-body-student--one-column': !isDialogTwoColumn,
        'material-preview-body-student--without-content': !content_type_guid,
      })}
    >
      {contentIsActivity(content_type_guid) ? (
        <div className="material-preview-body-student__activity">
          {!contentHasProgress(content_type_guid) && (
            <div className="material-preview-body-student__activity-status">
              <AttentionCard
                icon={attentionCardIcon}
                iconSize="3xl"
                title={t(`lesson:${attentionCardTitle}`, { duration: attentionCardDuration ? attentionCardDuration : null })}
                text1={t(`lesson:${attentionCardIconText}`)}
                iconColor="default"
                color={attentionCardColor}
              />
            </div>
          )}
          <div className="material-preview-body-student__activity-cover">
            <AssetImage
              className="lesson-item-card-student__image-img"
              id={!content_type_guid ? 'cover-card-material-without-content' : image}
              alt={t('alt:Activity thumbnail for {{activityName}}', { activityName: material.name })}
              src={didacticTypeCover || undefined}
            />
          </div>
          <div className="material-preview-body-student__activity-score">
            {contentHasProgress(content_type_guid, material) ? (
              <div className="material-preview-body-student__activity-progress">
                <div className="material-preview-body-student__activity-progress-wrapper">
                  <div className="material-preview-body-student__activity-progress-wrapper--progress-bar">
                    <ProgressBar hasPercentageLabel={false} isLabelHidden label={t('lesson:Progress')} progress={Math.min(progress * 100, 100)} />
                  </div>
                  <div className="progress-value" aria-hidden="true">
                    <div className="progress-value__number">{renderProgress}</div>
                    <div className="progress-value__percent">%</div>
                  </div>
                </div>
                {jobStatus === LESSON_ITEM_STATUS.EVALUATED && lessonItemScore?.score && (
                  <div className="material-preview-body-student__activity-score-status">
                    <Tag
                      className="score-tag"
                      customBackgroundColor={lessonItemScore?.color}
                      customTextColor={darkenColor(lessonItemScore?.color)}
                      label={lessonItemScore?.score}
                      size="lg"
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="material-preview-body-student__activity">
                {jobStatus === LESSON_ITEM_STATUS.EVALUATED && lessonItemScore?.score && (
                  <div className="material-preview-body-student__activity-score-status">
                    <Tag
                      className="score-tag"
                      customBackgroundColor={lessonItemScore?.color}
                      customTextColor={darkenColor(lessonItemScore?.color)}
                      label={lessonItemScore?.score}
                      size="lg"
                    />
                  </div>
                )}
                {jobStatus !== LESSON_ITEM_STATUS.EVALUATED && showStatus && (
                  <div
                    className={`material-preview-body-student__activity-score-status material-preview-body-student__activity-score-status--${activityStatus.style}`}
                  >
                    {t('lesson:' + activityStatus.text)}
                  </div>
                )}
                <div className="material-preview-body-student__activity-metadata">
                  {getMetadataInfo(material).map(({ icon, text, description, isLate }, index) => (
                    <div
                      key={index}
                      className={clsx('material-preview-body-student__activity-metadata-item', {
                        'material-preview-body-student__activity-metadata-item--separator': index !== 0,
                      })}
                    >
                      <IconBackground icon={icon} text={text} description={description} color={isLate ? 'red' : 'default'} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="material-preview-body-student__activity-button">
            <Button
              text={t(`lesson:${buttonText}`)}
              variant={buttonStyle === 'disabled' ? 'primary' : buttonStyle}
              size="lg"
              isDisabled={buttonIsDisabled}
              onClick={onClickStartActivity}
              isFullWidth
            />
          </div>
        </div>
      ) : (
        <>
          {useTangerineViewerInPreview && (
            <>
              {shouldUsePdfViewer && <PdfContent contentGuid={content_guid} onClickFullScreen={onClickOpenInNewTab} contentType={content_type_guid} />}
              {!shouldUsePdfViewer && (
                <ViewerContainer
                  config={{ pdfConfig: [], disabledFetchData: true }}
                  contentGuid={content_guid}
                  eventParams={{ course, from: 'lesson' }}
                  showEmbed
                  jobGuid={jobGuid}
                  pdfLibrary={PDF_ENGINE.NUTRIENT}
                  standalone={true}
                  shouldConsumption={!course?.isReadOnly}
                />
              )}
              {content_type_guid === CONTENT_TYPES.IMAGE && (
                <div className="material-preview-body-student__document-button">
                  <Button ariaLabel={t('common:View fullscreen')} isIconOnly leftIcon="expand" onClick={onClickOpenContent} size="sm" variant="primary" />
                </div>
              )}
            </>
          )}
          {!useTangerineViewerInPreview && content_type_guid === CONTENT_TYPES.HTML && (
            <MaterialPreviewViewer material={material} isStudent t={t} openResource={() => onClickOpen(material)} />
          )}
          {!useTangerineViewerInPreview && content_type_guid === CONTENT_TYPES.LINK && <MaterialPreviewLink course={course} material={material} />}
          {!content_type_guid && (
            <div className="dialog-material-preview__container-description">
              <TextHtml text={material?.description} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

MaterialPreviewBodyStudent.propTypes = {
  course: PropTypes.object.isRequired,
  material: PropTypes.object.isRequired,
  onClickOpenDetailedView: PropTypes.func,
  evaluationPeriodPublished: PropTypes.bool,
  t: PropTypes.func.isRequired,
  lessonItemScore: PropTypes.object,
  isDialogTwoColumn: PropTypes.bool,
};

export default MaterialPreviewBodyStudent;
